import React, { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

interface GeofenceMapProps {
  onGeofenceChange: (geofenceData: any) => void;
  initialGeofence?: string; // JSON string of existing geofence data
  height?: string;
}

const GeofenceMap: React.FC<GeofenceMapProps> = ({ 
  onGeofenceChange, 
  initialGeofence, 
  height = '400px' 
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const draw = useRef<MapboxDraw | null>(null);
  // const [isMapLoaded, setIsMapLoaded] = useState(false);

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    const handleDrawUpdate = () => {
      if (!draw.current) return;

      const data = draw.current.getAll();
      
      if (data.features.length > 0) {
        // Take the first (and should be only) polygon
        const feature = data.features[0];
        
        // Convert GeoJSON coordinates to the format expected by Flutter
        // feature.geometry.coordinates[0] is the outer ring of the polygon
        if (feature.geometry.type === 'Polygon') {
          const coordinates = (feature.geometry as any).coordinates[0];
          const polygonPoints = coordinates.map((coord: [number, number]) => ({
            latitude: coord[1],  // GeoJSON is [lng, lat], we need [lat, lng]
            longitude: coord[0]
          }));
          
          const geofenceData = {
            shape: 'polygon',
            trigger: 'enter', // Default trigger type, will be updated by parent
            polygonPoints: polygonPoints,
            isActive: true,
            id: `geofence_${Date.now()}`, // Generate a unique ID
            name: 'Geofence Area',
            description: 'Custom drawn geofence area'
          };
          
          onGeofenceChange(geofenceData);
        }
      } else {
        // No geofence drawn
        onGeofenceChange(null);
      }
    };

    // Initialize map
    map.current = new maplibregl.Map({
      container: mapContainer.current,
      center: [172.6, -43.5], // New Zealand coordinates as default
      zoom: 8,
      style: {
        version: 8,
        sources: {
          'osm': {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '© OpenStreetMap contributors'
          }
        },
        layers: [
          {
            id: 'background',
            type: 'background',
            paint: {
              'background-color': '#f8f9fa'
            }
          },
          {
            id: 'osm',
            type: 'raster',
            source: 'osm',
            paint: {}
          }
        ]
      }
    });

    // Add navigation controls
    map.current.addControl(new maplibregl.NavigationControl(), 'top-right');

    // Initialize Mapbox GL Draw
    draw.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true
      },
      defaultMode: 'draw_polygon'
    });

    map.current.addControl(draw.current as any);

    // Handle draw events
    map.current.on('draw.create', handleDrawUpdate);
    map.current.on('draw.delete', handleDrawUpdate);
    map.current.on('draw.update', handleDrawUpdate);

    map.current.on('load', () => {
      // setIsMapLoaded(true);
      
      // Load initial geofence if provided
      if (initialGeofence && draw.current) {
        try {
          const geofenceObj = JSON.parse(initialGeofence);
          if (geofenceObj.polygonPoints && geofenceObj.shape === 'polygon') {
            // Convert Flutter format back to GeoJSON
            const coordinates = geofenceObj.polygonPoints.map((point: any) => [
              point.longitude, // GeoJSON is [lng, lat]
              point.latitude
            ]);
            // Close the polygon by adding the first point at the end
            coordinates.push(coordinates[0]);
            
            draw.current.add({
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'Polygon',
                coordinates: [coordinates]
              }
            });
          }
        } catch (e) {
          console.error('Error parsing initial geofence data:', e);
        }
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [onGeofenceChange, initialGeofence]);

  const clearGeofence = () => {
    if (draw.current) {
      draw.current.deleteAll();
      onGeofenceChange(null);
    }
  };

  return (
    <div className="geofence-map-container">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-900">Geofence Area</h4>
          <p className="text-sm text-gray-600">Draw a polygon on the map to create a geofenced area for this message</p>
        </div>
        <button
          type="button"
          onClick={clearGeofence}
          className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border"
        >
          Clear Area
        </button>
      </div>
      
      <div 
        ref={mapContainer} 
        style={{ height }}
        className="w-full border border-gray-300 rounded-md"
      />
      
      <div className="mt-2 text-xs text-gray-500">
        <p>• Click and drag to draw a polygon area</p>
        <p>• Click the trash icon to delete the area</p>
        <p>• Users will receive this message when they enter the drawn area</p>
      </div>
    </div>
  );
};

export default GeofenceMap;